// Buyer Financial Analysis Types
// These types represent the buyer's personal financial state for SBA loan analysis

export interface CreditScore {
  bureau: 'Experian' | 'Equifax' | 'TransUnion';
  score: number;
  date: string;
  factors?: {
    paymentHistory: number;
    creditUtilization: number;
    lengthOfCreditHistory: number;
    newCredit: number;
    creditMix: number;
  };
}

export interface BankAccount {
  id: string;
  accountType: 'checking' | 'savings' | 'money_market' | 'cd' | 'investment';
  bankName: string;
  accountNumber: string; // Last 4 digits only for security
  currentBalance: number;
  averageBalance: number;
  monthlyInflow: number;
  monthlyOutflow: number;
  statementDate: string;
}

export interface Asset {
  id: string;
  type: 'real_estate' | 'vehicle' | 'investment' | 'business' | 'personal_property' | 'cash' | 'retirement';
  description: string;
  currentValue: number;
  originalCost?: number;
  acquisitionDate?: string;
  isLiquid: boolean;
  isCollateral: boolean;
  monthlyIncome?: number; // For income-producing assets
}

export interface Liability {
  id: string;
  type: 'mortgage' | 'auto_loan' | 'credit_card' | 'personal_loan' | 'business_loan' | 'student_loan' | 'other';
  description: string;
  originalAmount: number;
  currentBalance: number;
  monthlyPayment: number;
  interestRate: number;
  termMonths: number;
  remainingMonths: number;
  isBusinessRelated: boolean;
  collateral?: string;
}

export interface IncomeSource {
  id: string;
  type: 'salary' | 'business_income' | 'investment' | 'rental' | 'pension' | 'social_security' | 'other';
  description: string;
  monthlyAmount: number;
  annualAmount: number;
  isGuaranteed: boolean;
  startDate: string;
  endDate?: string;
  employer?: string;
}

export interface TaxReturn {
  year: number;
  filingStatus: 'single' | 'married_joint' | 'married_separate' | 'head_of_household' | 'qualifying_widow';
  adjustedGrossIncome: number;
  taxableIncome: number;
  totalTax: number;
  refund: number;
  w2Income: number;
  businessIncome: number;
  investmentIncome: number;
  otherIncome: number;
  deductions: number;
  exemptions: number;
  isFiled: boolean;
  filingDate?: string;
}

export interface SBAPersonalFinancialStatement {
  id: string;
  statementDate: string;
  personalInfo: {
    name: string;
    ssn: string; // Last 4 digits only
    address: string;
    phone: string;
    email: string;
    maritalStatus: 'single' | 'married' | 'divorced' | 'widowed';
    dependents: number;
  };
  assets: Asset[];
  liabilities: Liability[];
  incomeSources: IncomeSource[];
  monthlyNetWorth: number;
  debtToIncomeRatio: number;
  liquidityRatio: number;
}

export interface BuyerFinancialProfile {
  id: string;
  buyerId: string;
  profileDate: string;
  creditScores: CreditScore[];
  bankAccounts: BankAccount[];
  sbaPersonalFinancialStatement: SBAPersonalFinancialStatement;
  taxReturns: TaxReturn[];
  totalAssets: number;
  totalLiabilities: number;
  netWorth: number;
  monthlyIncome: number;
  monthlyExpenses: number;
  monthlyNetIncome: number;
  debtToIncomeRatio: number;
  creditUtilizationRatio: number;
  liquidityRatio: number;
  riskScore: number; // 1-100 scale
  sbaEligibilityScore: number; // 1-100 scale
  lastUpdated: string;
  metadata?: Record<string, any>;
}

// Analysis and calculation interfaces
export interface CreditAnalysis {
  averageScore: number;
  scoreRange: 'excellent' | 'good' | 'fair' | 'poor';
  riskFactors: string[];
  recommendations: string[];
  sbaImpact: 'positive' | 'neutral' | 'negative';
}

export interface DebtAnalysis {
  totalDebt: number;
  monthlyDebtPayments: number;
  debtToIncomeRatio: number;
  debtToAssetRatio: number;
  highRiskDebts: Liability[];
  recommendations: string[];
}

export interface LiquidityAnalysis {
  liquidAssets: number;
  monthlyLiquidityRatio: number;
  emergencyFundMonths: number;
  sbaLiquidityRequirement: number;
  meetsRequirement: boolean;
  recommendations: string[];
}

export interface IncomeAnalysis {
  totalMonthlyIncome: number;
  totalAnnualIncome: number;
  guaranteedIncome: number;
  variableIncome: number;
  incomeStability: 'high' | 'medium' | 'low';
  incomeTrend: 'increasing' | 'stable' | 'decreasing';
  recommendations: string[];
}

export interface BuyerFinancialAnalysis {
  profile: BuyerFinancialProfile;
  creditAnalysis: CreditAnalysis;
  debtAnalysis: DebtAnalysis;
  liquidityAnalysis: LiquidityAnalysis;
  incomeAnalysis: IncomeAnalysis;
  overallRiskScore: number;
  sbaEligibilityScore: number;
  recommendations: string[];
  strengths: string[];
  weaknesses: string[];
  nextSteps: string[];
}

// Utility functions
export function calculateDebtToIncomeRatio(monthlyDebtPayments: number, monthlyIncome: number): number {
  return monthlyIncome > 0 ? (monthlyDebtPayments / monthlyIncome) * 100 : 0;
}

export function calculateLiquidityRatio(liquidAssets: number, monthlyExpenses: number): number {
  return monthlyExpenses > 0 ? liquidAssets / monthlyExpenses : 0;
}

export function calculateNetWorth(assets: Asset[], liabilities: Liability[]): number {
  const totalAssets = assets.reduce((sum, asset) => sum + asset.currentValue, 0);
  const totalLiabilities = liabilities.reduce((sum, liability) => sum + liability.currentBalance, 0);
  return totalAssets - totalLiabilities;
}

export function getCreditScoreRange(score: number): 'excellent' | 'good' | 'fair' | 'poor' {
  if (score >= 750) return 'excellent';
  if (score >= 700) return 'good';
  if (score >= 650) return 'fair';
  return 'poor';
}

export function getCreditScoreColor(score: number): string {
  const range = getCreditScoreRange(score);
  switch (range) {
    case 'excellent': return 'text-green-600';
    case 'good': return 'text-blue-600';
    case 'fair': return 'text-yellow-600';
    case 'poor': return 'text-red-600';
    default: return 'text-gray-600';
  }
}

export function getRiskLevelColor(score: number): string {
  if (score >= 80) return 'text-green-600 bg-green-50';
  if (score >= 60) return 'text-yellow-600 bg-yellow-50';
  if (score >= 40) return 'text-orange-600 bg-orange-50';
  return 'text-red-600 bg-red-50';
}

export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
}

export function formatPercentage(value: number, decimals: number = 1): string {
  return `${value.toFixed(decimals)}%`;
}

// Sample data for development and testing
export const sampleBuyerFinancialProfile: BuyerFinancialProfile = {
  id: 'sample-profile-1',
  buyerId: 'buyer-123',
  profileDate: '2025-01-27',
  creditScores: [
    {
      bureau: 'Experian',
      score: 745,
      date: '2025-01-15',
      factors: {
        paymentHistory: 95,
        creditUtilization: 25,
        lengthOfCreditHistory: 85,
        newCredit: 90,
        creditMix: 80
      }
    },
    {
      bureau: 'Equifax',
      score: 738,
      date: '2025-01-15'
    },
    {
      bureau: 'TransUnion',
      score: 752,
      date: '2025-01-15'
    }
  ],
  bankAccounts: [
    {
      id: 'checking-1',
      accountType: 'checking',
      bankName: 'Chase Bank',
      accountNumber: '****1234',
      currentBalance: 45000,
      averageBalance: 42000,
      monthlyInflow: 8500,
      monthlyOutflow: 7200,
      statementDate: '2025-01-15'
    },
    {
      id: 'savings-1',
      accountType: 'savings',
      bankName: 'Chase Bank',
      accountNumber: '****5678',
      currentBalance: 125000,
      averageBalance: 118000,
      monthlyInflow: 2000,
      monthlyOutflow: 500,
      statementDate: '2025-01-15'
    }
  ],
  sbaPersonalFinancialStatement: {
    id: 'pfs-1',
    statementDate: '2025-01-27',
    personalInfo: {
      name: 'John Smith',
      ssn: '***-**-1234',
      address: '123 Main St, Anytown, ST 12345',
      phone: '(555) 123-4567',
      email: 'john.smith@email.com',
      maritalStatus: 'married',
      dependents: 2
    },
    assets: [
      {
        id: 'home-1',
        type: 'real_estate',
        description: 'Primary Residence',
        currentValue: 450000,
        originalCost: 380000,
        acquisitionDate: '2018-06-15',
        isLiquid: false,
        isCollateral: true
      },
      {
        id: 'vehicle-1',
        type: 'vehicle',
        description: '2020 Toyota Camry',
        currentValue: 25000,
        originalCost: 32000,
        acquisitionDate: '2020-03-10',
        isLiquid: false,
        isCollateral: true
      },
      {
        id: 'investment-1',
        type: 'investment',
        description: '401(k) Retirement Account',
        currentValue: 180000,
        isLiquid: false,
        isCollateral: false
      }
    ],
    liabilities: [
      {
        id: 'mortgage-1',
        type: 'mortgage',
        description: 'Primary Residence Mortgage',
        originalAmount: 350000,
        currentBalance: 280000,
        monthlyPayment: 1850,
        interestRate: 3.5,
        termMonths: 360,
        remainingMonths: 312,
        isBusinessRelated: false,
        collateral: 'Primary Residence'
      },
      {
        id: 'auto-loan-1',
        type: 'auto_loan',
        description: '2020 Toyota Camry Loan',
        originalAmount: 28000,
        currentBalance: 12000,
        monthlyPayment: 450,
        interestRate: 4.2,
        termMonths: 60,
        remainingMonths: 24,
        isBusinessRelated: false,
        collateral: '2020 Toyota Camry'
      }
    ],
    incomeSources: [
      {
        id: 'salary-1',
        type: 'salary',
        description: 'Software Engineer Salary',
        monthlyAmount: 8500,
        annualAmount: 102000,
        isGuaranteed: true,
        startDate: '2020-01-15',
        employer: 'Tech Corp Inc'
      },
      {
        id: 'spouse-salary-1',
        type: 'salary',
        description: 'Spouse - Marketing Manager',
        monthlyAmount: 4200,
        annualAmount: 50400,
        isGuaranteed: true,
        startDate: '2019-08-01',
        employer: 'Marketing Solutions LLC'
      }
    ],
    monthlyNetWorth: 0, // Will be calculated
    debtToIncomeRatio: 0, // Will be calculated
    liquidityRatio: 0 // Will be calculated
  },
  taxReturns: [
    {
      year: 2024,
      filingStatus: 'married_joint',
      adjustedGrossIncome: 152400,
      taxableIncome: 128500,
      totalTax: 18500,
      refund: 2400,
      w2Income: 152400,
      businessIncome: 0,
      investmentIncome: 0,
      otherIncome: 0,
      deductions: 23900,
      exemptions: 0,
      isFiled: true,
      filingDate: '2025-02-15'
    },
    {
      year: 2023,
      filingStatus: 'married_joint',
      adjustedGrossIncome: 145800,
      taxableIncome: 122300,
      totalTax: 17200,
      refund: 1800,
      w2Income: 145800,
      businessIncome: 0,
      investmentIncome: 0,
      otherIncome: 0,
      deductions: 23500,
      exemptions: 0,
      isFiled: true,
      filingDate: '2024-02-20'
    }
  ],
  totalAssets: 0, // Will be calculated
  totalLiabilities: 0, // Will be calculated
  netWorth: 0, // Will be calculated
  monthlyIncome: 0, // Will be calculated
  monthlyExpenses: 0, // Will be calculated
  monthlyNetIncome: 0, // Will be calculated
  debtToIncomeRatio: 0, // Will be calculated
  creditUtilizationRatio: 0, // Will be calculated
  liquidityRatio: 0, // Will be calculated
  riskScore: 0, // Will be calculated
  sbaEligibilityScore: 0, // Will be calculated
  lastUpdated: '2025-01-27T10:00:00Z'
};

// Calculate derived values for sample data
export function calculateBuyerFinancialMetrics(profile: BuyerFinancialProfile): BuyerFinancialProfile {
  const { sbaPersonalFinancialStatement, bankAccounts, creditScores } = profile;
  
  // Calculate total assets
  const totalAssets = sbaPersonalFinancialStatement.assets.reduce((sum, asset) => sum + asset.currentValue, 0);
  
  // Calculate total liabilities
  const totalLiabilities = sbaPersonalFinancialStatement.liabilities.reduce((sum, liability) => sum + liability.currentBalance, 0);
  
  // Calculate net worth
  const netWorth = totalAssets - totalLiabilities;
  
  // Calculate monthly income
  const monthlyIncome = sbaPersonalFinancialStatement.incomeSources.reduce((sum, income) => sum + income.monthlyAmount, 0);
  
  // Calculate monthly debt payments
  const monthlyDebtPayments = sbaPersonalFinancialStatement.liabilities.reduce((sum, liability) => sum + liability.monthlyPayment, 0);
  
  // Calculate monthly expenses (estimated as 60% of income for this example)
  const monthlyExpenses = monthlyIncome * 0.6;
  
  // Calculate monthly net income
  const monthlyNetIncome = monthlyIncome - monthlyExpenses;
  
  // Calculate debt-to-income ratio
  const debtToIncomeRatio = calculateDebtToIncomeRatio(monthlyDebtPayments, monthlyIncome);
  
  // Calculate liquidity ratio
  const liquidAssets = bankAccounts.reduce((sum, account) => sum + account.currentBalance, 0);
  const liquidityRatio = calculateLiquidityRatio(liquidAssets, monthlyExpenses);
  
  // Calculate average credit score
  const averageCreditScore = creditScores.reduce((sum, score) => sum + score.score, 0) / creditScores.length;
  
  // Calculate risk score (simplified algorithm)
  const riskScore = Math.max(0, Math.min(100, 
    100 - (averageCreditScore - 300) / 4 + // Credit score impact
    (debtToIncomeRatio > 40 ? 20 : 0) + // High DTI penalty
    (liquidityRatio < 3 ? 15 : 0) + // Low liquidity penalty
    (monthlyNetIncome < 0 ? 25 : 0) // Negative cash flow penalty
  ));
  
  // Calculate SBA eligibility score
  const sbaEligibilityScore = Math.max(0, Math.min(100,
    (averageCreditScore - 300) / 4 * 0.4 + // 40% credit score
    (debtToIncomeRatio < 40 ? 30 : 0) + // 30% DTI requirement
    (liquidityRatio >= 3 ? 20 : 0) + // 20% liquidity requirement
    (monthlyNetIncome > 0 ? 10 : 0) // 10% positive cash flow
  ));

  return {
    ...profile,
    totalAssets,
    totalLiabilities,
    netWorth,
    monthlyIncome,
    monthlyExpenses,
    monthlyNetIncome,
    debtToIncomeRatio,
    creditUtilizationRatio: 0, // Would need credit card data to calculate
    liquidityRatio,
    riskScore,
    sbaEligibilityScore,
    sbaPersonalFinancialStatement: {
      ...sbaPersonalFinancialStatement,
      monthlyNetWorth: netWorth,
      debtToIncomeRatio,
      liquidityRatio
    }
  };
}

